import { Component, ElementRef, HostListener, Injector, afterNextRender, effect, inject, signal, viewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';

import { AtlasService, AtlasTurn } from '../../service/atlas.service';
import { AtlasMarkdownPipe } from './atlas-markdown.pipe';

/** Composer stops growing here and starts scrolling internally instead. */
const COMPOSER_MAX_HEIGHT = 120;

/**
 * "Ask Atlas" — floating assistant, mounted once in the admin layout.
 */
@Component({
  selector: 'app-atlas-widget',
  standalone: true,
  imports: [CommonModule, FormsModule, AtlasMarkdownPipe],
  templateUrl: './atlas-widget.component.html',
  styleUrls: ['./atlas-widget.component.scss'],
  host: { class: 'atlas-widget' }
})
export class AtlasWidgetComponent {
  private atlas = inject(AtlasService);
  private injector = inject(Injector);

  private threadEl = viewChild<ElementRef<HTMLDivElement>>('thread');
  private promptEl = viewChild<ElementRef<HTMLTextAreaElement>>('prompt');

  readonly capabilities = [
    { icon: '📊', text: 'Trips & activity — counts, completion, by service tier or day' },
    { icon: '💰', text: 'Revenue & margin — net revenue, leakage, insurance cost' },
    { icon: '🧑‍✈️', text: 'Drivers & customers — per-driver / per-customer economics' },
    { icon: '🧾', text: 'Invoices — computed per driver or customer for a period' },
    { icon: '🔎', text: '"Where are we losing money?" — weakest-area diagnosis' }
  ];

  readonly suggestions = [
    'How many completed trips in the last 7 days?',
    'Where are we losing the most money this month?',
    'Insurance cost for this month',
    'Which drivers had the lowest net revenue last month?',
    'Revenue breakdown by service tier this month'
  ];

  open = signal(false);
  minimized = signal(false);
  messages = signal<AtlasTurn[]>([]);
  busy = signal(false);
  error = signal<string | null>(null);
  draft = '';

  constructor() {
    // Drop the caret into the composer whenever the panel becomes usable —
    // on open, and again on restore from minimised.
    effect(() => {
      if (this.open() && !this.minimized()) {
        this.afterRender(() => {
          this.promptEl()?.nativeElement.focus();
          this.scrollNow();
        });
      }
    });
  }

  toggle(): void {
    this.open.update((isOpen) => !isOpen);
    // Reopening from the launcher should never come back still collapsed.
    if (this.open()) this.minimized.set(false);
  }

  close(): void {
    this.open.set(false);
  }

  toggleMinimize(): void {
    this.minimized.update((isMin) => !isMin);
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.open()) this.close();
  }

  /** Enter sends; Shift+Enter falls through to the textarea as a newline. */
  onEnterKey(event: Event): void {
    event.preventDefault();
    this.send();
  }

  /** Grow the composer with its content, then let it scroll once it hits the cap. */
  autoGrow(): void {
    const el = this.promptEl()?.nativeElement;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, COMPOSER_MAX_HEIGHT)}px`;
  }

  send(text?: string): void {
    const question = (text ?? this.draft).trim();
    if (!question || this.busy()) return;

    // `history` is the turns BEFORE this question — the current one goes in `question`.
    const history = this.messages();
    this.draft = '';

    queueMicrotask(() => {
      this.error.set(null);
      this.busy.set(true);
      this.messages.update((m) => [...m, { role: 'user', content: question }]);
      this.resetComposer();
      this.scrollToBottom();
    });

    this.atlas.ask(question, history).subscribe({
      next: (res) =>
        queueMicrotask(() => {
          this.messages.update((m) => [...m, { role: 'assistant', content: res.answer }]);
          this.error.set(null);
          this.busy.set(false);
          this.scrollToBottom();
        }),
      error: (e: Error) =>
        queueMicrotask(() => {
          this.error.set(e.message);
          this.busy.set(false);
          this.scrollToBottom();
        })
    });
  }

  /** Start a fresh conversation — Atlas has no server-side session, history lives here. */
  clear(): void {
    this.messages.set([]);
    this.error.set(null);
    this.afterRender(() => this.promptEl()?.nativeElement.focus());
  }

  private resetComposer(): void {
    this.afterRender(() => {
      const el = this.promptEl()?.nativeElement;
      if (el) el.style.height = 'auto';
    });
  }

  /**
   * Must run AFTER Angular has rendered the new message — a microtask fires before
   * the DOM update, so it would measure the old scrollHeight and leave the latest
   * message below the fold.
   */
  private scrollToBottom(): void {
    this.afterRender(() => this.scrollNow());
  }

  private scrollNow(): void {
    const el = this.threadEl()?.nativeElement;
    if (el) el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
  }

  private afterRender(fn: () => void): void {
    afterNextRender(fn, { injector: this.injector });
  }
}
